type WithRetryOptions = {
  retries: number;
  delayMs: number;
};

/**
 * Retry a function with exponential backoff.
 * @param callback - The function to retry.
 * @param retries - The number of retries.
 * @param delayMs - The delay in milliseconds between retries.
 * @returns A function that will retry the callback with exponential backoff.
 */
export function withRetry<TArgs extends unknown[], TResult>(callback: (...args: TArgs) => Promise<TResult>, {
  retries,
  delayMs
}: WithRetryOptions): (...args: TArgs) => Promise<TResult> {
  return (...args: TArgs) => new Promise(async (resolve, reject) => {
    let attempt = 0;
    while (attempt <= retries) {
      try {
        const result = await callback(...args);
        return resolve(result); // Exit if the callback succeeds
      } catch (error) {
        if (attempt === retries) {
          return reject(error); // Exit if retries are exhausted
        }
        const backoff = delayMs * Math.pow(2, attempt); // Calculate exponential backoff
        await new Promise(r => setTimeout(r, backoff)); // Wait before retrying
        attempt++;
      }
    }
  });
}